/**
 * API Configuration Utilities
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { clearDeliverySessionClock } from "./deliverySession";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

/** Axios instance without auth interceptors — used only for token refresh */
const refreshClient = axios.create({
  baseURL: typeof BASE_URL === "string" ? BASE_URL.replace(/\/+$/, "") : "",
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

/** Proactive JWT refresh before expiry — avoids 401 bursts on reload and reduces spurious logouts */
let scheduledRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

export function clearScheduledAccessTokenRefresh(): void {
  if (scheduledRefreshTimeout != null) {
    clearTimeout(scheduledRefreshTimeout);
    scheduledRefreshTimeout = null;
  }
}

function decodeJwtExpMs(token: string): number | null {
  try {
    const seg = token.split(".")[1];
    if (!seg) return null;
    const padLen = (4 - (seg.length % 4)) % 4;
    const b64 = seg.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLen);
    const json = JSON.parse(atob(b64)) as { exp?: number };
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Schedule a silent refresh shortly before the access JWT expires.
 * Call after login, after a successful refresh, and when restoring a session.
 */
export function scheduleAccessTokenRefresh(): void {
  clearScheduledAccessTokenRefresh();
  const token = localStorage.getItem("access") || localStorage.getItem("token");
  if (!token) return;
  const expMs = decodeJwtExpMs(token);
  if (expMs == null) return;
  const refreshBeforeMs = 120_000;
  const delay = Math.max(25_000, expMs - Date.now() - refreshBeforeMs);
  scheduledRefreshTimeout = setTimeout(() => {
    scheduledRefreshTimeout = null;
    if (isRefreshing) {
      scheduleAccessTokenRefresh();
      return;
    }
    void tryRefreshAccessToken()
      .then(() => {
        scheduleAccessTokenRefresh();
      })
      .catch(() => {
        /* Next API call will 401; interceptor refreshes or clears session */
      });
  }, delay);
}

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else if (token) prom.resolve(token);
  });
  failedQueue = [];
}

function requestUrl(cfg: InternalAxiosRequestConfig): string {
  try {
    return axios.getUri(cfg);
  } catch {
    return `${cfg.baseURL || ""}${cfg.url || ""}`;
  }
}

/**
 * Unwrap ResponseFormatter / nested `data` until a layer contains JWT fields.
 * Backend refresh returns rotated `refresh_token` — must persist or next refresh fails.
 */
function unwrapRefreshPayload(body: unknown): Record<string, unknown> {
  let cur: unknown = body;
  for (let i = 0; i < 6 && cur && typeof cur === "object"; i++) {
    const o = cur as Record<string, unknown>;
    if (
      typeof o.access_token === "string" ||
      typeof o.access === "string" ||
      typeof o.refresh_token === "string" ||
      typeof o.refresh === "string"
    ) {
      return o;
    }
    const next = o.data;
    if (next == null || next === cur) break;
    cur = next;
  }
  return (typeof cur === "object" && cur !== null ? cur : {}) as Record<string, unknown>;
}

async function tryRefreshAccessToken(): Promise<string> {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) throw new Error("No refresh token");
  /** Body key must be `refresh` (RefreshTokenSerializer), not refresh_token */
  const res = await refreshClient.post("/auth/refresh/", { refresh });
  const body = unwrapRefreshPayload(res.data);
  const access =
    (body.access_token as string | undefined) ||
    (body.access as string | undefined) ||
    "";
  if (!access || typeof access !== "string") throw new Error("Invalid refresh response");
  const newRefresh =
    (body.refresh_token as string | undefined) ||
    (body.refresh as string | undefined) ||
    "";
  if (!newRefresh || typeof newRefresh !== "string") {
    throw new Error("Invalid refresh response: missing rotated refresh_token");
  }
  localStorage.setItem("access", access);
  localStorage.setItem("token", access);
  localStorage.setItem("refresh", newRefresh);
  return access;
}

export const getAuthToken = (): string | null => {
  return localStorage.getItem("access") || localStorage.getItem("token");
};

export const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const getBaseUrl = (): string => BASE_URL || "";

export const buildApiUrl = (endpoint: string): string => {
  const base = getBaseUrl();
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const cleanBase = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${cleanBase}${path}`;
};

/** Clear persisted auth/session data without notifying the app */
export const clearLocalSession = (): void => {
  clearScheduledAccessTokenRefresh();
  const keys = [
    "token", "access", "refresh", "username", "fullName", "phoneNumber",
    "userId", "employeeId", "partnerId", "storeId", "storeName", "roleName",
    "profile", "user", "accessAllowedTo", "selectedOrderIds", "selectedOrders",
  ];
  keys.forEach((k) => localStorage.removeItem(k));
  clearDeliverySessionClock();
};

/** Clear local session and notify app to show login */
export const clearSession = (): void => {
  clearLocalSession();
  window.dispatchEvent(new CustomEvent("auth-expired"));
};

/**
 * On 401: refresh access token with `refresh` and retry once.
 * Avoids logging out when the access JWT expires before the 24h app session window.
 */
export const initAuthInterceptor = (): void => {
  axios.interceptors.response.use(
    (res) => res,
    async (err: AxiosError) => {
      const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean };
      if (!original || err.response?.status !== 401) {
        return Promise.reject(err);
      }

      const url = requestUrl(original);
      if (url.includes("/auth/delivery-login") || url.includes("delivery-login")) {
        return Promise.reject(err);
      }
      if (/\/auth\/refresh\/?($|\?)/i.test(url)) {
        clearSession();
        return Promise.reject(err);
      }

      if (original._retry) {
        clearSession();
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return axios(original);
          })
          .catch((e) => Promise.reject(e));
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const token = await tryRefreshAccessToken();
        processQueue(null, token);
        isRefreshing = false;
        scheduleAccessTokenRefresh();
        original.headers.Authorization = `Bearer ${token}`;
        return axios(original);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        isRefreshing = false;
        clearSession();
        return Promise.reject(refreshErr);
      }
    }
  );
};
